import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Clock } from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
  { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
  { value: 'UTC', label: 'UTC' },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DURATIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
];

const TIME_OPTIONS = [
  '00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00',
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00',
  '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'
];

const Settings = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [availability, setAvailability] = useState({});
  const [meetingMessage, setMeetingMessage] = useState('');
  const [meetingDuration, setMeetingDuration] = useState(30);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders()
      });
      const user = response.data;
      
      setTimezone(user.timezone || 'America/Los_Angeles');
      setMeetingMessage(user.meetingMessage || '');
      setMeetingDuration(user.meetingDuration || 30);
      
      const availObj = {};
      if (user.availability) {
        user.availability.forEach(item => {
          availObj[item.day] = {
            timeFrom: item.timeFrom || '09:00',
            timeTo: item.timeTo || '17:00'
          };
        });
      }
      setAvailability(availObj);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (day) => {
    setAvailability(prev => {
      const newAvail = { ...prev };
      if (newAvail[day]) {
        delete newAvail[day];
      } else {
        newAvail[day] = { timeFrom: '09:00', timeTo: '17:00' };
      }
      return newAvail;
    });
  };

  const updateTimeRange = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const availArray = Object.keys(availability).map(day => ({
        day,
        timeFrom: availability[day].timeFrom,
        timeTo: availability[day].timeTo
      }));

      await axios.put(`${API}/meetings/availability`, {
        timezone,
        availability: availArray,
        meetingMessage,
        meetingDuration
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          currentPassword,
          newPassword
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-20">
          <p className="text-slate-600">Loading...</p>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Button
          variant="ghost"
          onClick={() => navigate('/digi-connect')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Digi Connect
        </Button>
      )}

        <Card className="p-8 bg-white shadow-lg">
          <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Meeting Settings
          </h1>

          {/* Timezone Selection */}
          <div className="mb-8">
            <Label className="text-lg font-semibold mb-3 block">Your Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Meeting Duration */}
          <div className="mb-8">
            <Label className="text-lg font-semibold mb-3 block">Default Meeting Duration</Label>
            <p className="text-sm text-slate-600 mb-3">Set your default meeting length for all bookings</p>
            <Select value={meetingDuration.toString()} onValueChange={(value) => setMeetingDuration(parseInt(value))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map(dur => (
                  <SelectItem key={dur.value} value={dur.value.toString()}>
                    {dur.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Availability by Day */}
          <div className="mb-8">
            <Label className="text-lg font-semibold mb-3 block">Available Days & Hours</Label>
            <p className="text-sm text-slate-600 mb-4">Set your working hours for each day</p>
            
            <div className="space-y-4">
              {DAYS.map(day => (
                <Card key={day} className="p-4 bg-slate-50 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={!!availability[day]}
                        onCheckedChange={() => toggleDay(day)}
                      />
                      <Label className="font-semibold text-slate-900">{day}</Label>
                    </div>
                  </div>

                  {availability[day] && (
                    <div className="ml-8 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-600" />
                        <Label className="text-sm">From:</Label>
                        <Select
                          value={availability[day].timeFrom}
                          onValueChange={(value) => updateTimeRange(day, 'timeFrom', value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center gap-2">
                        <Label className="text-sm">To:</Label>
                        <Select
                          value={availability[day].timeTo}
                          onValueChange={(value) => updateTimeRange(day, 'timeTo', value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>

          {/* Meeting Message */}
          <div className="mb-8">
            <Label className="text-lg font-semibold mb-3 block">Meeting Message</Label>
            <p className="text-sm text-slate-600 mb-3">This message will be included in meeting confirmation emails</p>
            <Textarea
              value={meetingMessage}
              onChange={(e) => setMeetingMessage(e.target.value)}
              placeholder="e.g., Looking forward to our meeting! Please prepare any questions you may have."
              rows={4}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-8"></div>

          {/* Change Password Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Change Password</h2>
            <p className="text-gray-600 mb-6">Update your password to keep your account secure</p>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold mb-2 block">Current Password</Label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold mb-2 block">New Password</Label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  minLength={6}
                  required
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold mb-2 block">Confirm New Password</Label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  minLength={6}
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={changingPassword}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
              >
                {changingPassword ? 'Changing Password...' : 'Change Password'}
              </Button>
            </form>
          </div>

          {/* Save Meeting Settings Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 text-lg rounded-full"
          >
            {saving ? 'Saving...' : 'Save Meeting Settings'}
          </Button>
        </Card>
    </>
  );

  if (embedded) {
    return (
      <div className="max-w-4xl mx-auto">
        {content}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {content}
      </div>
    </div>
  );
};

export default Settings;
